'use client';

import { useState, useEffect, useCallback } from 'react';
import { UserPlus, Check, X, MapPin } from 'lucide-react';
import { formatCents } from '@/lib/format';
import { Button } from '@/components/ui/Button';

interface OnboardingEntry {
    id: string;
    property_id: string;
    customer_name: string;
    address_summary: string;
    services_summary: string;
    total_price_cents: number;
    status: string;
    created_at: string;
}

export function NewCustomerQueue() {
    const [entries, setEntries] = useState<OnboardingEntry[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const res = await fetch('/api/field/onboarding');
            if (!res.ok) return;
            const data = await res.json();
            setEntries(data.entries || []);
        } catch { /* ignore */ } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Poll every 30s
    useEffect(() => {
        const interval = setInterval(load, 30000);
        return () => clearInterval(interval);
    }, [load]);

    const resolve = async (id: string, status: 'scheduled' | 'dismissed') => {
        try {
            await fetch('/api/field/onboarding', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, status }),
            });
            setEntries(prev => prev.filter(e => e.id !== id));
        } catch { /* ignore */ }
    };

    if (loading || entries.length === 0) return null;

    return (
        <div className="bg-zinc-900/80 border border-green-500/20 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
                <UserPlus className="w-5 h-5 text-green-400" />
                <span className="text-xs font-bold uppercase text-green-400 tracking-widest">
                    New Customers — Needs Routing
                </span>
                <span className="ml-auto bg-green-500/20 text-green-400 text-xs font-bold px-2 py-0.5 rounded-full">
                    {entries.length}
                </span>
            </div>
            <div className="space-y-3">
                {entries.map(entry => (
                    <div key={entry.id} className="bg-zinc-800/50 rounded-xl p-4 border border-white/5">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-white truncate">{entry.customer_name}</p>
                                <p className="text-xs text-zinc-400 flex items-center gap-1 mt-1">
                                    <MapPin size={12} className="shrink-0" />
                                    <span className="truncate">{entry.address_summary}</span>
                                </p>
                                <p className="text-xs text-zinc-500 mt-1">{entry.services_summary}</p>
                                {entry.total_price_cents > 0 && (
                                    <p className="text-xs text-green-400 font-bold mt-1">{formatCents(entry.total_price_cents)}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <Button
                                    onClick={() => resolve(entry.id, 'scheduled')}
                                    className="w-8 h-8 rounded-lg bg-green-500/20 hover:bg-green-500/30 flex items-center justify-center transition-colors"
                                    title="Mark as routed"
                                >
                                    <Check size={14} className="text-green-400" />
                                </Button>
                                <Button
                                    onClick={() => resolve(entry.id, 'dismissed')}
                                    className="w-8 h-8 rounded-lg bg-zinc-700/50 hover:bg-zinc-700 flex items-center justify-center transition-colors"
                                    title="Dismiss"
                                >
                                    <X size={14} className="text-zinc-400" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
